import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Users,
  FileText,
  ArrowLeft,
  Phone,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trash2,
  Eye,
  Loader2,
  RefreshCw,
  Leaf
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [appointments, setAppointments] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('appointments');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appointmentsRes, articlesRes] = await Promise.all([
        axios.get(`${API}/appointments`),
        axios.get(`${API}/articles?published_only=false`)
      ]);
      setAppointments(appointmentsRes.data);
      setArticles(articlesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateAppointmentStatus = async (id, newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/appointments/${id}`, { status: newStatus });
      setAppointments(prev =>
        prev.map(apt => (apt.id === id ? { ...apt, status: newStatus } : apt))
      );
      toast.success(`Appointment marked as ${newStatus}`);
      setSelectedAppointment(null);
    } catch (error) {
      console.error('Failed to update appointment:', error);
      toast.error('Failed to update appointment');
    } finally {
      setUpdating(false);
    }
  };

  const deleteAppointment = async (id) => {
    try {
      await axios.delete(`${API}/appointments/${id}`);
      setAppointments(prev => prev.filter(apt => apt.id !== id));
      toast.success('Appointment deleted');
      setDeleteTarget(null);
    } catch (error) {
      console.error('Failed to delete appointment:', error);
      toast.error('Failed to delete appointment');
    }
  };

  const deleteArticle = async (slug) => {
    try {
      await axios.delete(`${API}/articles/${slug}`);
      setArticles(prev => prev.filter(a => a.slug !== slug));
      toast.success('Article deleted');
      setDeleteTarget(null);
    } catch (error) {
      console.error('Failed to delete article:', error);
      toast.error('Failed to delete article');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-green-100 text-green-800 border-green-200',
      completed: 'bg-blue-100 text-blue-800 border-blue-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    const icons = {
      pending: <AlertCircle className="w-3 h-3" />,
      confirmed: <CheckCircle className="w-3 h-3" />,
      completed: <CheckCircle className="w-3 h-3" />,
      cancelled: <XCircle className="w-3 h-3" />
    };
    return (
      <Badge className={`${styles[status] || styles.pending} flex items-center gap-1`}>
        {icons[status] || icons.pending}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredAppointments = statusFilter === 'all'
    ? appointments
    : appointments.filter(apt => apt.status === statusFilter);

  const stats = {
    total: appointments.length,
    pending: appointments.filter(a => a.status === 'pending').length,
    confirmed: appointments.filter(a => a.status === 'confirmed').length,
    completed: appointments.filter(a => a.status === 'completed').length
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E6E2D8] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-[#6B6B6B] hover:text-[#2D4A3E]"
              data-testid="admin-back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Website
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#2D4A3E] flex items-center justify-center">
              <Leaf className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-[#2D4A3E]">Admin Dashboard</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-6 rounded-xl border border-[#E6E2D8]"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#2D4A3E]/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-[#2D4A3E]" />
              </div>
              <span className="text-[#6B6B6B] text-sm">Total</span>
            </div>
            <p className="text-3xl font-semibold text-[#2D4A3E]">{stats.total}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-xl border border-[#E6E2D8]"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <span className="text-[#6B6B6B] text-sm">Pending</span>
            </div>
            <p className="text-3xl font-semibold text-yellow-600">{stats.pending}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 rounded-xl border border-[#E6E2D8]"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-[#6B6B6B] text-sm">Confirmed</span>
            </div>
            <p className="text-3xl font-semibold text-green-600">{stats.confirmed}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-6 rounded-xl border border-[#E6E2D8]"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-[#6B6B6B] text-sm">Completed</span>
            </div>
            <p className="text-3xl font-semibold text-blue-600">{stats.completed}</p>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setActiveTab('appointments')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'appointments'
                ? 'bg-[#2D4A3E] text-white'
                : 'bg-white text-[#6B6B6B] hover:bg-[#E6E2D8]'
            }`}
            data-testid="tab-appointments"
          >
            <Calendar className="w-4 h-4" />
            Appointments
          </button>
          <button
            onClick={() => setActiveTab('articles')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'articles'
                ? 'bg-[#2D4A3E] text-white'
                : 'bg-white text-[#6B6B6B] hover:bg-[#E6E2D8]'
            }`}
            data-testid="tab-articles"
          >
            <FileText className="w-4 h-4" />
            Articles
          </button>

          <div className="ml-auto flex items-center gap-4">
            {activeTab === 'appointments' && (
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-white" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Button
              onClick={fetchData}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#2D4A3E]" />
          </div>
        ) : activeTab === 'appointments' ? (
          /* Appointments Table */
          <div className="bg-white rounded-xl border border-[#E6E2D8] overflow-hidden">
            {filteredAppointments.length === 0 ? (
              <div className="py-16 text-center text-[#6B6B6B]">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No appointments found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="appointments-table">
                  <thead className="bg-[#F5F2EB]">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-[#2D4A3E]">
                        Patient
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-[#2D4A3E]">
                        Phone
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-[#2D4A3E]">
                        Health Concern
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-[#2D4A3E]">
                        Date
                      </th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-[#2D4A3E]">
                        Status
                      </th>
                      <th className="text-right px-6 py-4 text-sm font-semibold text-[#2D4A3E]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppointments.map((apt, index) => (
                      <tr
                        key={apt.id}
                        className="border-t border-[#E6E2D8] hover:bg-[#F9F8F6]"
                        data-testid={`appointment-row-${index}`}
                      >
                        <td className="px-6 py-4">
                          <p className="font-medium text-[#2D4A3E]">{apt.name}</p>
                          <p className="text-xs text-[#6B6B6B]">{apt.source}</p>
                        </td>
                        <td className="px-6 py-4">
                          <a
                            href={`tel:${apt.phone}`}
                            className="flex items-center gap-2 text-[#2D4A3E] hover:text-[#C67D63]"
                          >
                            <Phone className="w-4 h-4" />
                            {apt.phone}
                          </a>
                        </td>
                        <td className="px-6 py-4 max-w-xs">
                          <p className="text-[#6B6B6B] truncate">
                            {apt.issue || 'Not specified'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-[#6B6B6B]">
                            <Clock className="w-4 h-4" />
                            {formatDate(apt.timestamp)}
                          </div>
                        </td>
                        <td className="px-6 py-4">{getStatusBadge(apt.status)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedAppointment(apt)}
                              data-testid={`view-apt-${index}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteTarget({ type: 'appointment', id: apt.id })}
                              data-testid={`delete-apt-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* Articles Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.length === 0 ? (
              <div className="col-span-full py-16 text-center text-[#6B6B6B]">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No articles found</p>
              </div>
            ) : (
              articles.map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl border border-[#E6E2D8] overflow-hidden"
                  data-testid={`article-card-${index}`}
                >
                  <img
                    src={article.image_url || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400'}
                    alt={article.title}
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">
                        {article.category}
                      </Badge>
                      {!article.published && (
                        <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800">
                          Draft
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-[#2D4A3E] mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-[#6B6B6B] line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <Link
                        to={`/articles/${article.slug}`}
                        className="text-sm text-[#C67D63] hover:underline"
                      >
                        View Article
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteTarget({ type: 'article', slug: article.slug })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}
      </main>

      {/* Appointment Detail Modal */}
      <Dialog open={!!selectedAppointment} onOpenChange={() => setSelectedAppointment(null)}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-xl text-[#2D4A3E]">
              Appointment Details
            </DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-[#6B6B6B]">Patient Name</label>
                <p className="font-medium text-[#2D4A3E]">{selectedAppointment.name}</p>
              </div>
              <div>
                <label className="text-sm text-[#6B6B6B]">Phone</label>
                <a
                  href={`tel:${selectedAppointment.phone}`}
                  className="flex items-center gap-2 font-medium text-[#2D4A3E] hover:text-[#C67D63]"
                >
                  <Phone className="w-4 h-4" />
                  {selectedAppointment.phone}
                </a>
              </div>
              <div>
                <label className="text-sm text-[#6B6B6B]">Health Concern</label>
                <p className="text-[#2D4A3E]">
                  {selectedAppointment.issue || 'Not specified'}
                </p>
              </div>
              <div>
                <label className="text-sm text-[#6B6B6B]">Submitted On</label>
                <p className="text-[#2D4A3E]">{formatDate(selectedAppointment.timestamp)}</p>
              </div>
              <div>
                <label className="text-sm text-[#6B6B6B]">Current Status</label>
                <div className="mt-1">{getStatusBadge(selectedAppointment.status)}</div>
              </div>

              <div className="pt-4 border-t border-[#E6E2D8]">
                <label className="text-sm text-[#6B6B6B] mb-2 block">Update Status</label>
                <div className="flex flex-wrap gap-2">
                  {['pending', 'confirmed', 'completed', 'cancelled'].map((status) => (
                    <Button
                      key={status}
                      variant={selectedAppointment.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, status)}
                      disabled={updating || selectedAppointment.status === status}
                      className={selectedAppointment.status === status ? 'bg-[#2D4A3E]' : ''}
                      data-testid={`status-btn-${status}`}
                    >
                      {updating ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        status.charAt(0).toUpperCase() + status.slice(1)
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the{' '}
              {deleteTarget?.type}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={() => {
                if (deleteTarget?.type === 'appointment') {
                  deleteAppointment(deleteTarget.id);
                } else if (deleteTarget?.type === 'article') {
                  deleteArticle(deleteTarget.slug);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
